import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import { Fragment, ReactNode, useState } from 'react';
import {
	ListItem,
	ListItemProps,
	Badge,
	styled,
	BadgeProps,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { ReactComponent as DotIcon } from '@/assets/icon/dot.svg';
import { useGetUser } from '@/hooks/useGetUser';
import { menuPermission } from '@/casl/menuPermission';
import { useRouteMatch } from '@/utils/routeHelper';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import SdCardOutlinedIcon from '@mui/icons-material/SdCardOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';

interface ListItemLinkProps extends ListItemProps {
	icon: ReactNode;
	to: string;
	badgeContent?: number;
	permission?: boolean;
}

const listItemNameMap: { [key: string]: string } = {
	'/user-manage': 'drawerListItems.userManage',
	'/user-manage/user-list/*': 'drawerListItems.userList',
	'/user-manage/delete-user-record/*': 'drawerListItems.deleteUserRecord',
	'/staff-manage': 'drawerListItems.staffManage',
	'/staff-manage/staff-list/*': 'drawerListItems.staffList',
	'/staff-manage/delete-staff-record/*': 'drawerListItems.deleteStaffRecord',
	'/app-setting': 'drawerListItems.appSetting',
	'/app-setting/clock-list/*': 'drawerListItems.clockList',
	'/information-manage': 'drawerListItems.informationManage',
	'/information-manage/emo-support-list/*': 'drawerListItems.emoSupportList',
	'/information-manage/faq-list/*': 'drawerListItems.faqList',
	'/information-manage/about-us-list/*': 'drawerListItems.aboutUsList',
	'/information-manage/information-list/*': 'drawerListItems.informationList',
	'/app-setting/notification-list/*': 'drawerListItems.notificationList',
	'/app-setting/moment-list/*': 'drawerListItems.momentList',
	'/app-setting/health-list/*': 'drawerListItems.healthList',
	'/app-setting/dialog-list/*': 'drawerListItems.dialogList',
	'/exercise-manage': 'drawerListItems.exerciseManage',
	'/exercise-manage/meditation-list/*': 'drawerListItems.meditation',
	'/exercise-manage/body-list/*': 'drawerListItems.body',
	'/exercise-manage/crossover-list/*': 'drawerListItems.crossover',
	'/exercise-manage/day21-list/*': 'drawerListItems.day21',
	'/exercise-manage/ding-day21/*': 'drawerListItems.exerciseSetting',
	'/exercise-manage/major-list/*': 'drawerListItems.marjorExercise',

	'/task-manage': 'drawerListItems.taskManage',
	'/task-manage/activity-list/*': 'drawerListItems.activityList',
	'/task-manage/achievement-list/*': 'drawerListItems.achievementList',
	'/task-manage/regular-day-list/*': 'drawerListItems.regularTask',
	'/task-manage/regular-week-list/*': 'drawerListItems.regularTask',
	'/task-manage/regular-month-list/*': 'drawerListItems.regularTask',
	'/task-manage/rule-list/*': 'drawerListItems.ruleList',

	'/treasury-manage': 'drawerListItems.treasuryManage',
	'/treasury-manage/chartlet-list/*': 'drawerListItems.chartletList',
	'/treasury-manage/wish-list/*': 'drawerListItems.wishList',
	'/treasury-manage/stamp-list/*': 'drawerListItems.stampList',
	'/treasury-manage/explain/*': 'drawerListItems.explainText',

	'/treasury-manage/puzzle-list/*': 'drawerListItems.puzzleList',
	'/treasury-manage/gallery-list/*': 'drawerListItems.galleryList',

	'/exchange-manage': 'drawerListItems.exchangeManage',
	'/exchange-manage/week-treasure/*': 'drawerListItems.weekTreasure',
	'/exchange-manage/mystery-box/*': 'drawerListItems.mysteryBox',

	'/report-manage': 'drawerListItems.reportManage',
	'/report-manage/report-list/*': 'drawerListItems.reportList',
};
interface rootMenu {
	permission: boolean;
	icon: ReactNode;
	to: string;
	nestedCollapseList: NestedMenuItem[];
}
interface NestedMenuItem {
	permission: boolean;
	to: string;
	badgeContent?: number;
}

interface ListItemsProps {
	drawerOpen: boolean;
	handleDrawerOpen: () => void;
}

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
	'& .MuiBadge-badge': {
		position: 'relative',
		top: 10,
		backgroundColor: '#FFDAD6',
		color: '#FF5449',
	},
}));

export default function ListItems({
	drawerOpen,
	handleDrawerOpen,
}: ListItemsProps) {
	const { userInfo } = useGetUser();

	const { t } = useTranslation();
	const { pathname } = useLocation();

	const routeMatch = useRouteMatch(pathname, Object.keys(listItemNameMap));
	const currentTab = routeMatch?.pattern?.path;
	console.log(currentTab, pathname);

	const [openIndex, setOpenIndex] = useState<number[]>([]);

	const rootMenus: rootMenu[] = [
		{
			permission: true,
			icon: <PersonOutlineRoundedIcon />,
			to: '/user-manage',
			nestedCollapseList: [
				{
					permission: true,
					to: '/user-manage/user-list',
				},
				{
					permission: true,
					to: '/user-manage/delete-user-record',
				},
			],
		},
		{
			permission: true,
			icon: <BadgeOutlinedIcon />,
			to: '/staff-manage',
			nestedCollapseList: [
				{
					permission: true,
					to: '/staff-manage/staff-list',
				},
				{
					permission: true,
					to: '/staff-manage/delete-staff-record',
				},
			],
		},
		{
			permission: true,
			icon: <FolderOpenOutlinedIcon />,
			to: '/information-manage',
			nestedCollapseList: [
				{
					permission: true,
					to: '/information-manage/information-list',
				},
				{
					permission: true,
					to: '/information-manage/faq-list',
				},
				{
					permission: true,
					to: '/information-manage/emo-support-list',
				},
				{
					permission: true,
					to: '/information-manage/about-us-list',
				},
			],
		},
		{
			permission: true,
			icon: <LibraryBooksOutlinedIcon />,
			to: '/exercise-manage',
			nestedCollapseList: [
				{
					permission: true,
					to: '/exercise-manage/day21-list',
				},
				{
					permission: true,
					to: '/exercise-manage/meditation-list',
				},
				{
					permission: true,
					to: '/exercise-manage/body-list',
				},
				{
					permission: true,
					to: '/exercise-manage/crossover-list',
				},
				{
					permission: true,
					to: '/exercise-manage/major-list',
				},
				{
					permission: true,
					to: '/exercise-manage/ding-day21',
				},
			],
		},
		{
			permission: true,
			icon: <SdCardOutlinedIcon />,
			to: '/app-setting',
			nestedCollapseList: [
				{
					permission: true,
					to: '/app-setting/clock-list',
				},
				{
					permission: true,
					to: '/app-setting/notification-list',
				},
				{
					permission: true,
					to: '/app-setting/moment-list',
				},
				{
					permission: true,
					to: '/app-setting/health-list',
				},
				{
					permission: true,
					to: '/app-setting/dialog-list',
				},
			],
		},
		{
			permission: true,
			icon: <AssignmentOutlinedIcon />,
			to: '/task-manage',
			nestedCollapseList: [
				{
					permission: true,
					to: '/task-manage/regular-day-list',
				},
				{
					permission: true,
					to: '/task-manage/achievement-list',
				},

				{
					permission: true,
					to: '/task-manage/activity-list',
				},
				{
					permission: true,
					to: '/task-manage/rule-list',
				},
			],
		},
		{
			permission: true,
			icon: <AutoAwesomeOutlinedIcon />,
			to: '/treasury-manage',
			nestedCollapseList: [
				{
					permission: true,
					to: '/treasury-manage/gallery-list',
				},
				{
					permission: true,
					to: '/treasury-manage/puzzle-list',
				},
				{
					permission: true,
					to: '/treasury-manage/stamp-list',
				},
				{
					permission: true,
					to: '/treasury-manage/wish-list',
				},
				{
					permission: true,
					to: '/treasury-manage/chartlet-list',
				},
				{
					permission: true,
					to: '/treasury-manage/explain',
				},
			],
		},
		{
			permission: true,
			icon: <CurrencyExchangeIcon />,
			to: '/exchange-manage',
			nestedCollapseList: [
				{
					permission: true,
					to: '/exchange-manage/mystery-box',
				},
				{
					permission: true,
					to: '/exchange-manage/week-treasure',
				},
			],
		},
		{
			permission: true,
			icon: <AssessmentOutlinedIcon />,
			to: '/report-manage',
			nestedCollapseList: [
				{
					permission: true,
					to: '/report-manage/report-list',
				},
			],
		},
	];

	const handleOpenIndex = (index: number) => {
		handleDrawerOpen();
		if (drawerOpen && openIndex.includes(index)) {
			setOpenIndex((oldValues) => {
				return oldValues.filter((value) => value !== index);
			});
		} else if (drawerOpen) {
			setOpenIndex([...openIndex, index]);
		} else {
			setOpenIndex([index]);
		}
	};

	function SingleListItem(props: ListItemLinkProps) {
		const { icon, to, sx, badgeContent, permission } = props;
		const pathMatch =
			useRouteMatch(to, Object.keys(listItemNameMap))?.pattern.path ?? '';
		const primary = listItemNameMap[pathMatch];

		const listItemButtonProps = { component: Link, to: to };

		if (permission && menuPermission[userInfo?.role]) {
			const permission = menuPermission[userInfo?.role].find((item: string) => {
				if (
					item === to ||
					(item.endsWith('/*') && to.startsWith(item.slice(0, -2)))
				) {
					return true;
				} else {
					return false;
				}
			});
			if (!permission) return <></>;
		}

		return (
			<>
				<ListItem component='li' disablePadding>
					<ListItemButton
						{...listItemButtonProps}
						selected={currentTab == pathMatch}
						sx={{
							justifyContent: drawerOpen ? 'initial' : 'center',
							px: 2.5,
							...sx,
						}}
					>
						<ListItemIcon
							sx={{
								minWidth: 0,
								mr: drawerOpen ? 3 : 'auto',
								justifyContent: 'center',
								alignItems: 'center',
								flexDirection: 'column',
								color:
									currentTab == pathMatch ? 'primary.main' : 'text.primary',
							}}
						>
							{icon}
							{!drawerOpen && (
								<Typography
									variant='subtitle1'
									style={{
										maxWidth: '60px',
										textAlign: 'center',
										whiteSpace: 'normal',
										display: '-webkit-box',
										WebkitLineClamp: 2,
										WebkitBoxOrient: 'vertical',
										overflow: 'hidden',
									}}
								>
									{t(primary)}
								</Typography>
							)}
						</ListItemIcon>
						<ListItemText
							primary={t(primary)}
							sx={{
								whiteSpace: 'normal',
								display: '-webkit-box',
								WebkitLineClamp: 2,
								WebkitBoxOrient: 'vertical',
								overflow: 'hidden',
								opacity: drawerOpen ? 1 : 0,
								color:
									currentTab == pathMatch ? 'primary.main' : 'text.primary',
							}}
						/>

						{badgeContent !== undefined && badgeContent > 0 && (
							<StyledBadge badgeContent={badgeContent}></StyledBadge>
						)}
					</ListItemButton>
				</ListItem>
			</>
		);
	}

	return (
		<List>
			{rootMenus.map((menu, index) => {
				const pathMatch =
					useRouteMatch(menu.to, Object.keys(listItemNameMap))?.pattern.path ??
					'';
				const primary = listItemNameMap[pathMatch];
				if (menu.permission && menuPermission[userInfo?.role]) {
					const permission = menuPermission[userInfo?.role].find(
						(item: string) => {
							if (
								item === menu.to ||
								(item.endsWith('/*') && menu.to.startsWith(item.slice(0, -2)))
							) {
								return true;
							} else {
								return false;
							}
						}
					);
					if (!permission) return;
				}

				return (
					<Fragment key={index}>
						<ListItem
							component='li'
							onClick={() => handleOpenIndex(index)}
							disablePadding
						>
							<ListItemButton
								sx={{
									justifyContent: drawerOpen ? 'initial' : 'center',
									px: 2.5,
								}}
							>
								<ListItemIcon
									sx={{
										minWidth: 0,
										mr: drawerOpen ? 3 : 'auto',
										justifyContent: 'center',
										alignItems: 'center',
										flexDirection: 'column',
										color: 'text.primary',
									}}
								>
									<Badge
										sx={{
											'& .MuiBadge-badge': {
												backgroundColor: '#FF5449',
											},
										}}
										variant='dot'
										invisible={
											!menu.nestedCollapseList.some(
												(value: { to: string; badgeContent?: number }) => {
													return (
														value.badgeContent !== undefined &&
														value.badgeContent > 0
													);
												}
											)
										}
									>
										{menu.icon}
									</Badge>
									{!drawerOpen && (
										<Typography
											variant='subtitle1'
											style={{
												maxWidth: '60px',
												textAlign: 'center',
												whiteSpace: 'normal',
												display: '-webkit-box',
												WebkitLineClamp: 2,
												WebkitBoxOrient: 'vertical',
												overflow: 'hidden',
											}}
										>
											{t(primary)}
										</Typography>
									)}
								</ListItemIcon>
								<ListItemText
									primary={t(primary)}
									sx={{
										opacity: drawerOpen ? 1 : 0,
										color: 'text.primary',
									}}
								/>
								{drawerOpen &&
									(openIndex.includes(index) ? <ExpandLess /> : <ExpandMore />)}
							</ListItemButton>
						</ListItem>
						{menu.nestedCollapseList && drawerOpen && (
							<Collapse
								component='li'
								in={openIndex.includes(index)}
								timeout='auto'
								unmountOnExit
							>
								<List disablePadding>
									{menu.nestedCollapseList.map((subMenu, subIndex) => (
										<SingleListItem
											key={subIndex}
											icon={<DotIcon />}
											to={subMenu.to}
											badgeContent={subMenu.badgeContent}
											permission={subMenu.permission}
											sx={{
												pointerEvents: pathname.includes(subMenu.to)
													? 'none'
													: 'auto',
											}}
										/>
									))}
								</List>
							</Collapse>
						)}
					</Fragment>
				);
			})}
		</List>
	);
}
